import sanitizeHtml from 'sanitize-html';
import { useParams } from '@/providers/RouterShim';
import type { Branch } from '../../../../@types/Branch';
import VehicleDescription from '../../../../routes/sales-and-purchasing/Items/VehicleDescription';
import { Column, Columns } from '../../../../styled-components/components/Columns';
import { H5 } from '../../../../styled-components/components/Heading';
import Highlight from '../../../../styled-components/components/Highlight';
import { IconExternalLink } from '../../../../styled-components/components/Icon';
import { Text } from '../../../../styled-components/components/Text';
import type { QuickSearchResultListItem, QuickSearchResultListSection } from '..';
import Item, { AiIconWrapper, AiItem, Items } from './Items';

interface SectionsProps {
  query: string;
  sections: QuickSearchResultListSection[];
  useAi: boolean;
  branches: Branch[];
  getItemRef: (sectionIndex: number, itemIndex: number) => (node: HTMLLIElement) => void;
  handleItemClick: (item: QuickSearchResultListItem) => void;
  handleItemHover: (sectionIndex: number, itemIndex: number) => void;
}

const stripHtml = (htmlString: string): string =>
  sanitizeHtml(htmlString, { allowedTags: [], allowedAttributes: {} });

const Sections = ({
  sections,
  query,
  branches,
  getItemRef,
  handleItemClick,
  handleItemHover,
}: SectionsProps) => {
  const { branchId } = useParams();
  if (!sections.length) {
    return null;
  }

  const renderItem =
    (sectionIndex: number) => (item: QuickSearchResultListItem, itemIndex: number) => {
      switch (item.type) {
        case 'vehicle':
          if (!item.vehicle) {
            return null;
          }

          return (
            <Item
              key={item.url}
              ref={getItemRef(sectionIndex, itemIndex)}
              $active={item.isActive}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => handleItemHover(sectionIndex, itemIndex)}
            >
              <VehicleDescription
                vehicle={item.vehicle}
                isVisible={true}
                isParent={false}
                branchId={branchId}
                highlight={query}
                branches={branches}
              />
            </Item>
          );
        case 'ai-toggle':
          return (
            <AiItem
              key="ai-toggle"
              ref={getItemRef(sectionIndex, itemIndex)}
              $active={item.isActive}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => handleItemHover(sectionIndex, itemIndex)}
            >
              <Columns $valign="start" $spacing={1}>
                <Column>
                  <AiIconWrapper>{item.icon}</AiIconWrapper>
                </Column>
                <Column $grow>
                  <Text $size="small" $weight="bold">
                    {item.header}
                  </Text>
                  <Text $size="xsmall">{item.title}</Text>
                </Column>
              </Columns>
            </AiItem>
          );
        default:
          return (
            <Item
              key={item.url}
              ref={getItemRef(sectionIndex, itemIndex)}
              $active={item.isActive}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() => handleItemHover(sectionIndex, itemIndex)}
            >
              <Columns $valign="center" $spacing={2}>
                {item.icon && <Column $noShrink>{item.icon}</Column>}
                <Column $grow>
                  {item.header && (
                    <Text $size="xsmall">
                      {item.doNotHighlight || item.type === 'page' ? (
                        item.header
                      ) : (
                        <Highlight text={item.header} highlight={query} />
                      )}
                    </Text>
                  )}

                  {item.doNotHighlight ? (
                    item.title
                  ) : (
                    <Highlight text={stripHtml(item.title)} highlight={query} />
                  )}
                </Column>
                {item.isExternal && (
                  <Column $noShrink>
                    <IconExternalLink />
                  </Column>
                )}
              </Columns>
            </Item>
          );
      }
    };

  return (
    <>
      {sections
        .filter((section) => section.items.length)
        .map((section, sectionIndex) => {
          const itemRenderer = renderItem(sectionIndex);

          return (
            <div key={section.title}>
              <H5 as="div" $margin="none">
                {section.title}
              </H5>
              {section.subtitle && <Text $size="small">{section.subtitle}</Text>}

              <Items>{section.items.map(itemRenderer)}</Items>
            </div>
          );
        })}
    </>
  );
};
export default Sections;
